<script setup>
import { useObjectsStore } from '@/store/objects'
import { ref, inject, onMounted, watchEffect } from 'vue'
import { FilterMatchMode } from '@primevue/core/api'
import { useRouter } from 'vue-router'

const objectStore = useObjectsStore()

const windowTooSmall = inject('windowTooSmall')
const dialogRef = inject('dialogRef')
const router = useRouter()
const loading = ref(true)
const post = ref(null)
const error = ref(null)

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))
onMounted(() => {
  fetchData()
})

async function fetchData () {
  error.value = post.value = null
  loading.value = true
  try {
    await sleep(0)
    if (objectStore.availableObjects.length === 0) {
      await objectStore.fetchAvailableObjects()
    }
  } catch (err) {
    error.value = err.toString()
  } finally {
    loading.value = false
  }
}

const filters = ref()
const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  }
}
initFilters()

const selectedObject = ref()
watchEffect(() => {
  if (selectedObject.value) {
    navigateToObjectMapping(selectedObject.value.apiName)
  }
})

const navigateToObjectMapping = (sObjectAPIName) => {
  router.push(`/objects/${sObjectAPIName}`)
  dialogRef.value.close()
}

</script>

<template>
  <div class="max-h-full flex-grow flex flex-col min-h-0 pt-2">
    <div class="flex justify-between items-center h-8">
      <h1
        v-if="!windowTooSmall"
        class="leading-6 flex items-center"
      >
        Select an object to proceed:
      </h1>
      <div
        class="flex gap-2 md:flex-grow-0"
        :class="{'flex-grow': windowTooSmall}"
      >
        <IconField class="w-full">
          <InputIcon>
            <i class="pi pi-search" />
          </InputIcon>
          <InputText
            v-model="filters['global'].value"
            v-focus.timeout
            class="w-full"
            placeholder="Object Name Search"
          />
        </IconField>
      </div>
    </div>
    <div
      v-if="loading"
      class="flex flex-1 w-full items-center justify-center"
    >
      <ProgressSpinner />
    </div>
    <div
      v-else
      class="flex-grow pt-4 -mx-4 md:mx-0 rounded-lg bg-[--p-content-background]"
    >
      <DataTable
        v-model:filters="filters"
        v-model:selection="selectedObject"
        scrollable
        scroll-height="flex"
        removable-sort
        :global-filter-fields="['apiName','label']"
        :value="objectStore.availableObjects"
        table-style="height: 100%; width: 100%; max-width: 100%;"
        data-key="apiName"
        :virtual-scroller-options="{ itemSize: windowTooSmall ? 75.5 : 58.29, numToleratedItems: 50 }"
        selection-mode="single"
        :meta-key-selection="false"
      >
        <template #empty>
          No mappings found
        </template>
        <Column
          field="label"
          sortable
          style="max-width: 50px;"
          header="Object"
        >
          <template #body="{ data }">
            <div class="flex flex-row items-center gap-2">
              <SfIcon
                icon=""
                color=""
              />
              <div class="flex-grow overflow-hidden">
                <div class="text-ellipsis overflow-hidden whitespace-nowrap w-full">
                  {{ data.label }}
                </div>
                <div class="text-sm text-ellipsis overflow-hidden whitespace-nowrap w-full">
                  {{ data.apiName }}
                </div>
                <div v-if="windowTooSmall">
                  {{ data.numSFRows }} row(s)
                </div>
              </div>
            </div>
          </template>
        </Column>
        <Column
          v-if="!windowTooSmall"
          field="numSFRows"
          sortable
          style="width: 250px"
          header="Salesforce rows"
        >
          <template #body="{ data }">
            <div class="flex flex-row items-center gap-2">
              {{ data.numSFRows }}
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<style scoped>
  .p-datatable-scrollable-table>.p-datatable-tfoot {
    bottom: -1px
  }
</style>
